/**
 * The analytics reporter (D154) — the shell's one outbound seam: the events a
 * session derives, batched and handed to the Worker.
 *
 * @remarks
 * App shell, where the network belongs. The engine derives the events from
 * committed state (`deriveSessionEvents` — a pure diff, no second log); this
 * module subscribes to the store, holds the previous state, batches what the
 * diff yields, and sends the batch with `navigator.sendBeacon` — the one
 * call a browser promises to finish after the tab is gone — falling back to
 * `fetch` where a beacon is refused. Nothing here reads the transcript
 * beyond what the diff reads; nothing here is retried; a Worker that is
 * down costs the demo nothing but a row.
 *
 * **Identity: none.** The session id is `crypto.randomUUID()` at boot,
 * never persisted, never sent anywhere else. A `?seed=` session is counted
 * and TAGGED (the marketing link and a bug report should not read as a
 * player), and a restored save is tagged `resumed`.
 *
 * **Opt-out (Mike, 2026-09-07): none for the demo — and the Global Privacy
 * Control / Do Not Track check is built and switched OFF**
 * (`REPORTER_POLICY.doesHonorPrivacySignals` below). Flipping it makes the
 * reporter inert for a browser that signals either; the store's privacy
 * page decides when that flips.
 *
 * @packageDocumentation
 */

import type { LoadedGameBundle } from '@orpheus/content-loader';
import type { AnalyticsBatch, AnalyticsEvent } from '@orpheus/content-schema';
import type { LocalHost } from '@orpheus/player-host';

import { deriveSessionEvents } from '@orpheus/engine-runtime';

/**
 * The reporter's one policy: whether a Global Privacy Control or Do Not
 * Track signal silences it. OFF by ruling for the demo; the check itself is
 * live below, so turning it on is one word.
 */
export const REPORTER_POLICY: { readonly doesHonorPrivacySignals: boolean } = { doesHonorPrivacySignals: false };

/** How long a batch waits for company before it goes — one turn's worth of events, not one event's worth of requests. */
const FLUSH_DELAY_MS = 2000;

/** The schema's cap on a batch; a longer run flushes early. */
const BATCH_LIMIT = 200;

/** What the reporter needs to know about the session it reports. */
export interface AnalyticsReporterOptions {
	/** The Worker's events URL; absent (no `VITE_ANALYTICS_URL`), the reporter is inert and the demo runs exactly as before. */
	readonly endpoint: string | undefined;
	/**
	 * The local host, whose commit stream carries the `(before, after)` pair
	 * the tally diff reads (D156). A server game's turns are reported by the
	 * platform, which holds both states; this reporter is the client mode's.
	 */
	readonly host: LocalHost;
}

/** Whether the browser is signaling a privacy preference the reporter would honor. */
export const isPrivacySignaled = (): boolean => {
	// Read by name: GPC is not in the DOM lib yet, and DNT is on its way out of
	// it — neither is a property the type system should vouch for.
	const signals = navigator as unknown as Readonly<Record<string, unknown>>;

	return signals['globalPrivacyControl'] === true || signals['doNotTrack'] === '1';
};

/** Send one batch, preferring the beacon; the promise never rejects — a lost row is not an error. */
const send = (endpoint: string, batch: AnalyticsBatch): void => {
	const body = JSON.stringify(batch);

	if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(endpoint, body)) {
		return;
	}

	void fetch(endpoint, { body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, method: 'POST' }).catch(() => undefined);
};

/**
 * Connect the reporter to a booted session.
 *
 * @param options - {@link AnalyticsReporterOptions}
 * @returns A function that flushes what is pending and stops reporting
 */
export const connectAnalyticsReporter = ({ endpoint, host }: AnalyticsReporterOptions): (() => void) => {
	if (endpoint === undefined || (REPORTER_POLICY.doesHonorPrivacySignals && isPrivacySignaled())) {
		return () => undefined;
	}

	const sessionId = crypto.randomUUID();
	// D164: the bundle is the commit's — a port can change it mid-sitting — so
	// a batch is stamped with the version its events ran on, and a change of
	// version flushes what came before it.
	let manifest: LoadedGameBundle['contentPack']['manifest'] | undefined;
	let pending: AnalyticsEvent[] = [];
	let flushTimer: ReturnType<typeof setTimeout> | undefined;

	const flush = (): void => {
		clearTimeout(flushTimer);
		flushTimer = undefined;

		if (pending.length === 0 || manifest === undefined) {
			return;
		}

		const events = pending;

		pending = [];
		send(endpoint, { events, gameId: manifest.gameId, schemaVersion: manifest.schemaVersion, sessionId });
	};
	const enqueue = (bundle: LoadedGameBundle, events: readonly AnalyticsEvent[]): void => {
		if (manifest !== bundle.contentPack.manifest) {
			flush();
			manifest = bundle.contentPack.manifest;
		}

		pending.push(...events);

		if (pending.length >= BATCH_LIMIT) {
			flush();

			return;
		}

		flushTimer ??= setTimeout(flush, FLUSH_DELAY_MS);
	};
	const unsubscribe = host.subscribe((commit) => {
		// The opening is one event; a turn is its tally diff; Begin again starts a
		// new diff baseline whose events are still this reporting session's — one
		// browser sitting.
		if (commit.kind === 'opened') {
			enqueue(commit.bundle, [{ kind: 'session_started', resumed: commit.wasResumed, seeded: commit.wasSeeded }]);
		} else if (commit.kind === 'turn') {
			enqueue(commit.bundle, deriveSessionEvents(commit.bundle, commit.before, commit.after));
		}
	});
	const onHidden = (): void => {
		if (document.visibilityState === 'hidden') {
			flush();
		}
	};

	document.addEventListener('visibilitychange', onHidden);

	return () => {
		unsubscribe();
		document.removeEventListener('visibilitychange', onHidden);
		flush();
	};
};
